import * as THREE from 'three'

import { ESCORTS } from '../world/taskGroup.js'
import { Wake, bowWave } from './Wake.js'
import { DEG, FT_TO_M } from '../util/units.js'

/**
 * The screen: the destroyer and cruiser keeping station on the carrier.
 *
 * They exist for scale. An empty ocean gives the eye nothing to measure
 * against — the sea has no features, the horizon has no landmarks, and a ship
 * a mile away looks the same as a ship five miles away. Two more hulls on the
 * water, each with a wake pointing the same way as the carrier's, and the
 * whole picture acquires depth and a direction.
 *
 * Deliberately simple geometry. At a mile and a half they are a few hundred
 * pixels of grey; what sells them is the silhouette and the wake, not rivets.
 */

const HULL = 0x4e5a62
const DECK = 0x3f4a52
const TOP = 0x5d6870
const FUNNEL = 0x33393e

export class EscortModel {
  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'Escorts'
    this.wakes = []
    this._bowWaves = []

    // Five paints, shared by every ship and every part.
    //
    // The first version called `new MeshStandardMaterial` once per part, so two
    // escorts of eight parts each meant sixteen materials describing five
    // distinct finishes. Three.js keys its shader programs and its render
    // batching off material identity, so identical-looking materials are not
    // free — each one is its own program lookup and its own draw call, and
    // nothing sharing a colour can ever be batched. Grey paint is grey paint.
    this.materials = {
      hull: this._mat(HULL),
      deck: this._mat(DECK),
      top: this._mat(TOP),
      bridge: this._mat(TOP, 0.7),
      funnel: this._mat(FUNNEL),
    }

    for (const escort of ESCORTS) {
      this.group.add(this._buildShip(escort))
    }
  }

  _mat(color, roughness = 0.85) {
    return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.15 })
  }

  _buildShip(escort) {
    const ship = new THREE.Group()
    ship.name = escort.name
    ship.position.set(escort.y * FT_TO_M, 0, -escort.x * FT_TO_M)

    const L = escort.lengthFt * FT_TO_M
    const B = escort.beamFt * FT_TO_M

    // ---- hull ---------------------------------------------------------
    // A box with a wedge bow. Crude, but from a mile away a warship IS a low
    // grey wedge with things on top of it.
    const hull = new THREE.Mesh(new THREE.BoxGeometry(B, B * 0.42, L * 0.86), this.materials.hull)
    hull.position.y = B * 0.12
    ship.add(hull)

    const bow = new THREE.Mesh(
      new THREE.ConeGeometry(B * 0.5, L * 0.22, 4),
      this.materials.hull,
    )
    // Euler order is XYZ, so Z is applied FIRST — putting the 45 degrees on Z
    // tilted the cone's axis rather than spinning its square cross-section,
    // and the +X quarter turn then pointed it aft. The result was a full-beam
    // grey slab lying diagonally across the front of the ship. Yaw on Y,
    // pitch on X, in that order.
    bow.rotation.set(-Math.PI / 2, Math.PI / 4, 0)
    bow.position.set(0, B * 0.12, -L * 0.52)
    bow.scale.set(1, 1, 0.42)
    ship.add(bow)

    // Main deck.
    const deck = new THREE.Mesh(new THREE.BoxGeometry(B * 0.94, B * 0.06, L * 0.9), this.materials.deck)
    deck.position.y = B * 0.33
    ship.add(deck)

    // ---- superstructure ------------------------------------------------
    const house = new THREE.Mesh(
      new THREE.BoxGeometry(B * 0.62, B * 0.7, L * 0.24),
      this.materials.top,
    )
    house.position.set(0, B * 0.68, -L * 0.06)
    ship.add(house)

    const bridge = new THREE.Mesh(
      new THREE.BoxGeometry(B * 0.5, B * 0.34, L * 0.1),
      this.materials.bridge,
    )
    bridge.position.set(0, B * 1.15, -L * 0.14)
    ship.add(bridge)

    // Mast — the tallest thing on a destroyer and most of its silhouette.
    const mast = new THREE.Mesh(
      new THREE.CylinderGeometry(B * 0.03, B * 0.05, B * 1.5, 6),
      this.materials.top,
    )
    mast.position.set(0, B * 1.9, -L * 0.1)
    ship.add(mast)

    // Funnel, offset aft.
    const funnel = new THREE.Mesh(
      new THREE.BoxGeometry(B * 0.32, B * 0.5, L * 0.08),
      this.materials.funnel,
    )
    funnel.position.set(0, B * 0.95, L * 0.1)
    ship.add(funnel)

    // Hangar and flight deck aft — every modern escort has one.
    const hangar = new THREE.Mesh(
      new THREE.BoxGeometry(B * 0.66, B * 0.44, L * 0.16),
      this.materials.top,
    )
    hangar.position.set(0, B * 0.58, L * 0.26)
    ship.add(hangar)

    // ---- water ---------------------------------------------------------
    const wake = new Wake({
      lengthFt: escort.lengthFt * 9,
      beamFt: escort.beamFt,
      opacity: 0.42,
      sternFt: 0,
    })
    wake.mesh.position.z = L * 0.45
    ship.add(wake.group)
    this.wakes.push(wake)

    const bw = bowWave({ beamFt: escort.beamFt, lengthFt: escort.lengthFt * 0.5, opacity: 0.4 })
    bw.position.set(0, 0.3, -L * 0.42)
    ship.add(bw)
    this._bowWaves.push(bw)

    // Escorts steer the same course as the carrier — that is what keeping
    // station means — so they need no heading of their own. A degree or two of
    // difference reads as a ship that is about to leave the formation.
    ship.rotation.y = 0
    return ship
  }

  /**
   * Escorts hold station, so they move exactly with the carrier and share its
   * heading. Their positions are already expressed in the ship's frame; this
   * just puts that frame where the carrier is.
   */
  sync(carrier) {
    this.group.position.copy(carrier.position)
    this.group.position.y = 0
    this.group.rotation.set(0, -carrier.headingDeg * DEG, 0)
  }

  dispose() {
    this.wakes.forEach((w) => w.dispose())
    Object.values(this.materials).forEach((m) => m.dispose())
    // The bow wave owns a canvas texture, and Material.dispose() does not free
    // a material's map — two leaked textures per run without this.
    for (const bw of this._bowWaves) {
      bw.material.map?.dispose()
      bw.material.dispose()
      bw.geometry.dispose()
    }
    this.group.traverse((o) => {
      if (!this.wakes.some((w) => w.mesh === o) && !this._bowWaves.includes(o)) {
        o.geometry?.dispose?.()
      }
    })
  }
}
